import styled from 'styles/styled';
import { ButtonFilled } from 'components/ui/Button';

// Mobile-first wrapper for the lobby/share screen
export const Screen = styled.main`
  display: flex;
  justify-content: center;
  padding: 24px 16px;
  min-height: calc(100vh - 85px);
`;

// Central container aligned with WalletAddressEntry GradientSection
export const Card = styled.section`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;
  background: linear-gradient(180deg, #15191e 0%, #000000 15%, #000000 33%, #15191e 100%);
  border-radius: 24px;
  text-align: center;
  color: #efefef;

  @media (max-width: 720px) {
    padding: 24px 16px;
    border-radius: 0 16px 16px 16px;
  }
`;

/**
 * Headline — DS-2 Screen 5 (Success). 36/700/-0.01em on desktop,
 * shrinks to 26 on mobile. The `span` carries the bull-green emphasis
 * for the "successfully" word.
 */
export const Headline = styled.h1`
  font-size: 36px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
  text-wrap: balance;
  margin: 12px 0 10px;

  span {
    color: #52d381;
  }

  @media (max-width: 720px) {
    font-size: 26px;
    margin-bottom: 8px;
  }
`;

/**
 * Description — DS-2 Screen 5 sub copy. 13/300/#A7A8B2 (fg-2),
 * centred with a max width so it never spans the full card.
 */
export const Description = styled.p`
  font-size: 13px;
  font-weight: 300;
  line-height: 1.5;
  color: #a7a8b2;
  text-align: center;
  max-width: 520px;
  margin: 0 auto 28px;
`;

/**
 * SuccessQRBox — DS-2 success-qr framed wrapper around the QR code.
 * Surface-1 background with a thin neutral border (no #696969 border —
 * that read too heavy in the original; DS uses rgba 0.06).
 */
export const SuccessQRBox = styled.div`
  width: 248px;
  height: 248px;
  margin: 0 auto 16px;
  padding: 28px;
  background: #15191e;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 720px) {
    width: 220px;
    height: 220px;
    padding: 18px;
  }
`;

// Wrapper for QR code (centered) — kept as a simple flex container,
// SuccessQRBox does the visual heavy lifting now.
export const QRWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0 0;
`;

/**
 * LinkSection — DS-2 dlink-block: stacked label + url + actions,
 * tight 10 px gap between rows, centred.
 */
export const LinkSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 8px auto 18px;
`;

export const LinkLabel = styled.h3`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: #efefef;
  margin: 0;
`;

/**
 * LinkValue — DS-2 dlink-url: monospace, 13/fg-2, slight tracking.
 * `user-select: all` so a single click selects the whole URL for
 * keyboard copy-paste; `word-break` lets long URLs wrap inside the
 * card without horizontal scroll.
 */
export const LinkValue = styled.p`
  font-family: 'SF Mono', 'Roboto Mono', Consolas, Menlo, monospace;
  font-size: 13px;
  font-weight: 400;
  color: #a7a8b2;
  letter-spacing: 0.01em;
  line-height: 1.45;
  text-align: center;
  word-break: break-all;
  max-width: 720px;
  margin: 0;
  user-select: all;
`;

export const ActionsRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;

  &.copy-qr-row {
    margin: 0 0 28px;
  }
`;

/**
 * ActionButton — DS-2 ghost CTA. Used for Copy QR, Copy link,
 * Share link rows on the success screen. Replaces the previous
 * filled #292B2E variant which read too solid for tertiary
 * actions next to the primary Play CTA.
 */
export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border: 1px solid #696969;
  background: transparent;
  color: #efefef;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: #8a8a8a;
  }

  &:focus-visible {
    outline: 2px solid #52d381;
    outline-offset: 2px;
  }
`;

/**
 * PlayWrapper — DS-2 success-play: centres the primary Play CTA
 * with breathing room above (margin-top 20) and below.
 */
export const PlayWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

/**
 * PlayButton — DS-2 success-play .btn: 220 px minimum width, 56 px
 * tall, 10 px radius. Stays centred instead of stretching like the
 * legacy ButtonRow > ButtonWrapper variant.
 */
export const PlayButton = styled(ButtonFilled)`
  flex: none;
  min-width: 220px;
  height: 56px;
  border-radius: 10px;
`;
